// Employee/components/View/DayEventCard.tsx
import React, {useCallback} from "react";
import {Box, Paper, Typography, useMediaQuery, useTheme as useMuiTheme,} from "@mui/material";
import {styles} from "../../styles/DayViewStyles";
import {Event, EventAddData} from "../../types";
import {addMinutes, differenceInMinutes, format, parseISO} from "date-fns";
import {useTheme} from "../../../Theme/ThemeContext";
import {useNavigate} from "react-router-dom";

interface DayEventCardProps {
    event: Event;
    onEdit: (event: Event) => void;
    isStartOfEvent: boolean;
    isEndOfEvent: boolean;
    userType: "client" | "psychologist" | "observer";
    onAdd: (eventData: EventAddData) => void;
    timeSlotStart: Date;
    timeSlotEnd: Date;
    canEdit: boolean;
}

const DayEventCard: React.FC<DayEventCardProps> = ({
                                                       event,
                                                       onEdit,
                                                       isStartOfEvent,
                                                       isEndOfEvent,
                                                       userType,
                                                       onAdd,
                                                       timeSlotStart,
                                                       timeSlotEnd,
                                                       canEdit,
                                                   }) => {
    const eventStart = parseISO(event.start);
    const eventEnd = parseISO(event.end);
    const mui_theme = useMuiTheme();
    const {plt} = useTheme();
    const isMobile = useMediaQuery(mui_theme.breakpoints.down("sm"));
    const componentStyles = styles(mui_theme);
    const navigate = useNavigate();
    const showButton =
        userType !== "observer" &&
        ((userType === "psychologist" && canEdit) ||
            (userType === "client" && event.status === "AVAILABLE"));

    const handleAction = useCallback(() => {
        if (event.status !== "AVAILABLE") {
            navigate(`/consultations/${event.id}/`);
            return;
        }

        if (userType === "psychologist" && canEdit) {
            onEdit(event);
        } else if (userType === "client" && event.status === "AVAILABLE" && onAdd) {
            const newEventStart = timeSlotStart > eventStart ? timeSlotStart : eventStart;
            const maxDuration = Math.min(
                60,
                differenceInMinutes(timeSlotEnd, newEventStart)
            );
            const newEventEnd = addMinutes(newEventStart, maxDuration);

            if (newEventEnd <= eventEnd && newEventEnd <= timeSlotEnd) {
                onAdd({
                    originalEvent: event,
                    proposedStart: newEventStart,
                    proposedEnd: newEventEnd,
                    timeSlotStart,
                    timeSlotEnd,
                });
            }
        }
    }, [
        event,
        navigate,
        userType,
        canEdit,
        onEdit,
        onAdd,
        timeSlotStart,
        timeSlotEnd,
        eventStart,
        eventEnd,
    ]);

    const handleInteraction = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            e.stopPropagation();
            handleAction();
        },
        [handleAction]
    );

    const addTransparency = (color: string, opacity: number): string => {
        if (color.startsWith("#")) {
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        }
        return color;
    };

    return (
        <Paper
            elevation={3}
            sx={{
                ...componentStyles.eventCard,
                backgroundColor: event.color
                    ? addTransparency(event.color, 0.7)
                    : componentStyles.eventCard.backgroundColor,
                outline: "none",
                transition: "all 0.3s ease-in-out",
                position: "relative",
                borderTopLeftRadius: isStartOfEvent ? "4px" : 0,
                borderTopRightRadius: isStartOfEvent ? "4px" : 0,
                borderBottomLeftRadius: isEndOfEvent ? "4px" : 0,
                borderBottomRightRadius: isEndOfEvent ? "4px" : 0,
            }}
            onClick={handleInteraction}
            onTouchStart={handleInteraction}
        >
            {/* Main content of the event card */}
            <Box sx={{...componentStyles.eventCardText}}>
                {isStartOfEvent && (
                    <>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                ...componentStyles.eventTitle,
                                color: plt.mode === "dark" ? "white" : "white",
                            }}
                        >
                            {event.title}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                ...componentStyles.eventTime,
                                color: plt.mode === "dark" ? "white" : "white",
                            }}
                        >
                            {format(eventStart, "HH:mm")} - {format(eventEnd, "HH:mm")}
                        </Typography>
                    </>
                )}
                {/* Event time slot */}
                {isEndOfEvent && differenceInMinutes(eventEnd, eventStart) >= 120 && (
                    <Typography
                        variant="body2"
                        sx={{
                            ...componentStyles.eventTime,
                            color: plt.mode === "dark" ? "white" : "white",
                        }}
                    >
                        {format(eventStart, "HH:mm")} - {format(eventEnd, "HH:mm")}
                    </Typography>
                )}
            </Box>
        </Paper>
    );
};

export default DayEventCard;
